import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@gapartners/ui';
import { StoredTranscriptView } from '@/components/amazon-connect/StoredTranscriptView';
import { TranscriptContent } from '@/components/amazon-connect/TranscriptContent';
import { mapLiveChunksToSegments } from '@/components/amazon-connect/transcriptUtils';
import { api } from '@/store/api';
import { useManagerTranscriptMonitor } from '@/hooks/useManagerTranscriptMonitor';
import { getInitialOrCaptureContactId } from '@/components/amazon-connect/contactUtils';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import type { ConnectContact } from '@/types';
import type { DisplayCall } from './CallHistoryCardTypes';
import { MixedAudioPlayer } from './MixedAudioPlayer';
import { CaseHistoryRow } from './CaseHistoryRow';
import { HistoricalCallCustomerLinkSection } from './HistoricalCallCustomerLinkSection';
import { useAppDispatch } from '@/store/hooks';
import { useAmazonConnectContacts } from '@/hooks/useAmazonConnectContacts';
import { setSessionCaseHistory } from '@/store/slices/callSlice';
import { formatCallerDisplayName } from '@/utils/customer';
import { ClientSpaceWorkspaceLink } from './ClientSpaceWorkspaceLink';
import {
  isUnansweredHistoricalCall,
  shouldOfferCreateCase,
} from './callHistoryUtils';
const {
  useLazyGetCallRecordingPresignedUrlQuery,
  useGetCallByContactIdQuery,
  useStartCallMonitoringMutation,
} = api;

dayjs.extend(duration);

export interface CallHistoryCallDetailsDialogProps {
  selectedCall: DisplayCall | null;
  showTranscript: boolean;
  setShowTranscript: (value: boolean) => void;
  onClose: () => void;

  getOutcomeColor: (outcome: string | null) => {
    bgcolor: string;
    color: string;
  };
  getCaseStatusColor: (caseStatus: string | null) => {
    bgcolor: string;
    color: string;
  };

  onOpenCaseId: (caseId: number, contactId: string) => void;
  onOpenCreateCase?: () => void;
  onCustomerLinked?: () => void;
  /** Show the customer link / change workspace section. */
  enableCustomerLink?: boolean;
  /** Missed-call details: label the call as missed. */
  isMissedCall?: boolean;
  enableLiveMonitoring?: boolean;
}

export function CallHistoryCallDetailsDialog(
  props: CallHistoryCallDetailsDialogProps
) {
  const {
    selectedCall,
    showTranscript,
    setShowTranscript,
    onClose,
    getOutcomeColor,
    getCaseStatusColor,
    onOpenCaseId,
    onOpenCreateCase,
    onCustomerLinked,
    enableCustomerLink = false,
    isMissedCall = false,
    enableLiveMonitoring = false,
  } = props;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { activeContacts } = useAmazonConnectContacts();

  const [
    triggerGetRecordingUrl,
    {
      data: recordingData,
      isFetching: isRecordingFetching,
      isError: isRecordingError,
    },
  ] = useLazyGetCallRecordingPresignedUrlQuery();
  const { data: latestCallByContact } = useGetCallByContactIdQuery(
    { contactId: selectedCall?.contactId ?? '' },
    { skip: !selectedCall }
  );

  // Sync caseHistory from API response to Redux so CustomerInfoCard can display it
  useEffect(() => {
    if (latestCallByContact?.contactId && latestCallByContact.caseHistory) {
      dispatch(
        setSessionCaseHistory({
          contactId: latestCallByContact.contactId,
          caseHistory: latestCallByContact.caseHistory,
        })
      );
    }
  }, [
    latestCallByContact?.contactId,
    latestCallByContact?.caseHistory,
    dispatch,
  ]);

  const [triggerStartCallMonitoring, { isLoading: isStartingCallMonitoring }] =
    useStartCallMonitoringMutation();

  const recordingPresignedUrls = recordingData?.recordings ?? [];
  const recordingExpiresAtKey = recordingPresignedUrls
    .map(recording => recording.expiresAt)
    .join('|');

  // Prevent showing recording status / audio for a different selected call.
  const [recordingForContactId, setRecordingForContactId] = useState<
    string | null
  >(null);
  const [monitoringRequestedForContactId, setMonitoringRequestedForContactId] =
    useState<string | null>(null);
  const [
    monitoringSessionContactIdByTarget,
    setMonitoringSessionContactIdByTarget,
  ] = useState<Record<string, string>>({});
  const [listeningStartedAtByContactId, setListeningStartedAtByContactId] =
    useState<Record<string, number>>({});
  const [monitoringRequestError, setMonitoringRequestError] = useState<
    string | null
  >(null);
  const [monitoringStopError, setMonitoringStopError] = useState<string | null>(
    null
  );
  const [isStoppingCallMonitoring, setIsStoppingCallMonitoring] =
    useState(false);
  const [tick, setTick] = useState(0);
  const selectedContactId = selectedCall?.contactId;

  useEffect(() => {
    setRecordingForContactId(null);
    setMonitoringRequestedForContactId(null);
    setMonitoringSessionContactIdByTarget({});
    setListeningStartedAtByContactId({});
    setMonitoringRequestError(null);
    setMonitoringStopError(null);
    setTick(0);
  }, [selectedCall?.contactId]);

  useEffect(() => {
    if (!recordingForContactId) return;
    if (!selectedContactId) return;
    if (recordingForContactId !== selectedContactId) return;
    if (!recordingExpiresAtKey) return;

    const interval = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(interval);
  }, [recordingForContactId, selectedContactId, recordingExpiresAtKey]);

  const showRecordingStatus =
    !!selectedContactId &&
    !!recordingForContactId &&
    recordingForContactId === selectedContactId;

  const handleLoadRecording = () => {
    if (!selectedCall) return;
    setRecordingForContactId(selectedCall.contactId);
    triggerGetRecordingUrl(selectedCall.contactId);
  };

  type ExpiryState = {
    isExpired: boolean;
    countdownParts: { minutes: number; seconds: number } | null;
  } | null;

  /** Return minutes and seconds from remaining ms for translated countdown */
  function getExpiryCountdownParts(remainingMs: number): {
    minutes: number;
    seconds: number;
  } | null {
    if (remainingMs <= 0) return null;
    const d = dayjs.duration(remainingMs);
    return { minutes: d.minutes(), seconds: d.seconds() };
  }

  function getRecordingExpiryLabel(expiryState: ExpiryState): string | null {
    if (!expiryState) return null;
    if (expiryState.isExpired) {
      return t('components.contactCenter.recordingExpired');
    }
    if (expiryState.countdownParts && expiryState.countdownParts.minutes >= 1) {
      return t('components.contactCenter.recordingExpiresInMinSec', {
        minutes: expiryState.countdownParts.minutes,
        seconds: expiryState.countdownParts.seconds,
      });
    }
    if (expiryState.countdownParts) {
      return t('components.contactCenter.recordingExpiresInSecOnly', {
        seconds: expiryState.countdownParts.seconds,
      });
    }
    return null;
  }

  function getExpiryState(expiresAt: string | undefined): ExpiryState {
    if (!expiresAt) return null;
    // Include tick as a no-op multiplier so rerenders depend on it.
    const now = Date.now() + tick * 0;
    const remainingMs = new Date(expiresAt).getTime() - now;
    const countdownParts = getExpiryCountdownParts(remainingMs);
    return {
      isExpired: remainingMs <= 0,
      countdownParts,
    };
  }

  const hasFreshCallState = Boolean(latestCallByContact?.contactId);
  const fallbackLiveFromSelection = selectedCall
    ? !selectedCall.endDate
    : false;
  const isLiveCall = hasFreshCallState
    ? latestCallByContact?.endDate == null
    : fallbackLiveFromSelection;
  // Calls opened from the missed-call list are always missed, so treat them
  // as unanswered regardless of the contact's end time / picked-up agents.
  const isUnanswered = selectedCall
    ? isMissedCall || isUnansweredHistoricalCall(selectedCall)
    : false;
  const showHandledBy = selectedCall ? selectedCall.pickedBy.length > 0 : false;
  const resolvedWorkspaceId =
    latestCallByContact?.workspaceId ?? selectedCall?.workspaceId;
  const caseHistory = latestCallByContact?.caseHistory;
  const showCreateCaseButton =
    !isLiveCall &&
    onOpenCreateCase &&
    shouldOfferCreateCase({
      enableCustomerLink,
      workspaceId: resolvedWorkspaceId,
      caseId: selectedCall?.caseId,
      caseHistory,
    });
  const shouldUseManagerLiveMonitor = enableLiveMonitoring && isLiveCall;
  // Outbound calls have no Boost metadata — flag the manager monitor stream
  // so the backend skips the Boost fetch. Same derivation as NotesCard.
  const activeContact = activeContacts.find(c => {
    try {
      return c.getContactId?.() === selectedContactId;
    } catch {
      return false;
    }
  });
  const isOutboundCall = Boolean(activeContact && !activeContact.isInbound());
  const { state: managerMonitorState, restart: restartManagerMonitor } =
    useManagerTranscriptMonitor(
      shouldUseManagerLiveMonitor && showTranscript
        ? selectedContactId
        : undefined,
      shouldUseManagerLiveMonitor && showTranscript,
      isOutboundCall
    );
  const managerLiveSegments = mapLiveChunksToSegments(
    managerMonitorState.transcriptChunks,
    {}
  );
  const managerTranscriptScrollRef = useRef<HTMLDivElement | null>(null);
  const callStatusLabel = isLiveCall
    ? t('pages.manager.callHistory.live')
    : isMissedCall
      ? t('components.contactCenter.missed')
      : t('pages.manager.callHistory.historic');
  const callStatusStyle = isLiveCall
    ? { bgcolor: '#dcfce7', color: '#166534' }
    : isMissedCall
      ? { bgcolor: '#fef9c3', color: '#854d0e' }
      : { bgcolor: '#e0e7ff', color: '#3730a3' };
  const managerMonitorStatusLabel = (() => {
    if (managerMonitorState.status === 'connecting') {
      return t('components.contactCenter.managerMonitorStatusConnecting');
    }
    if (managerMonitorState.status === 'live') {
      return t('components.contactCenter.managerMonitorStatusLive');
    }
    if (managerMonitorState.status === 'reconnecting') {
      return t('components.contactCenter.managerMonitorStatusReconnecting');
    }
    if (managerMonitorState.status === 'ended') {
      return t('components.contactCenter.managerMonitorStatusEnded');
    }
    if (managerMonitorState.status === 'error') {
      return t('components.contactCenter.managerMonitorStatusError');
    }
    return t('components.contactCenter.managerMonitorStatusIdle');
  })();
  const hasMonitoringRequestSuccess =
    !!selectedContactId &&
    monitoringRequestedForContactId === selectedContactId;
  const listeningStartedAt = selectedContactId
    ? listeningStartedAtByContactId[selectedContactId]
    : undefined;
  const listeningElapsedSeconds = listeningStartedAt
    ? Math.max(0, Math.floor((Date.now() - listeningStartedAt) / 1000))
    : 0;
  const listeningMinutes = Math.floor(listeningElapsedSeconds / 60);
  const listeningSeconds = listeningElapsedSeconds % 60;
  const listeningDurationLabel = `${String(listeningMinutes).padStart(2, '0')}:${String(listeningSeconds).padStart(2, '0')}`;

  useEffect(() => {
    if (!hasMonitoringRequestSuccess) return;
    const interval = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(interval);
  }, [hasMonitoringRequestSuccess]);

  const handleStartCallMonitoring = async () => {
    if (!selectedContactId) return;
    setMonitoringRequestError(null);
    setMonitoringStopError(null);
    try {
      const monitorSession = await triggerStartCallMonitoring({
        contactId: selectedContactId,
        allowedMonitorCapabilities: ['SILENT_MONITOR'],
      }).unwrap();
      connectAdapter.setExpectedMonitor(
        monitorSession.contactId,
        selectedContactId
      );
      setMonitoringRequestedForContactId(selectedContactId);
      setMonitoringSessionContactIdByTarget(prev => ({
        ...prev,
        [selectedContactId]: monitorSession.contactId,
      }));
      setListeningStartedAtByContactId(prev => ({
        ...prev,
        [selectedContactId]: Date.now(),
      }));
    } catch {
      connectAdapter.clearExpectedMonitor();
      setMonitoringRequestedForContactId(null);
      setMonitoringSessionContactIdByTarget(prev => {
        const next = { ...prev };
        delete next[selectedContactId];
        return next;
      });
      setListeningStartedAtByContactId(prev => {
        const next = { ...prev };
        delete next[selectedContactId];
        return next;
      });
      setMonitoringRequestError(
        t('components.contactCenter.listenCallRequestFailed')
      );
    }
  };

  const handleStopCallMonitoring = async () => {
    if (!selectedContactId) return;
    setMonitoringStopError(null);
    setIsStoppingCallMonitoring(true);
    try {
      const agent = connectAdapter.getCcpEnums().createAgent();
      const contacts = agent.getContacts();
      const sessionContactId =
        monitoringSessionContactIdByTarget[selectedContactId];
      const monitoringContact =
        contacts.find(contact => contact.getContactId() === sessionContactId) ??
        contacts.find(
          contact => getInitialOrCaptureContactId(contact) === selectedContactId
        );
      if (!monitoringContact) {
        throw new Error('Monitoring contact not found');
      }
      await new Promise<void>((resolve, reject) => {
        const contactObj = monitoringContact as ConnectContact;
        const monitoringType =
          connectAdapter.getCcpEnums().ConnectionType.MONITORING;
        const monitoringConn = contactObj.getConnections().find(conn => {
          const type = conn.getType?.();
          return monitoringType !== undefined
            ? type === monitoringType
            : String(type).toLowerCase() === 'monitoring';
        });
        if (monitoringConn?.destroy) {
          monitoringConn.destroy({
            success: () => resolve(),
            failure: err =>
              reject(
                err ?? new Error('Failed to destroy monitoring connection')
              ),
          });
          return;
        }
        const monitorAgentConnection = contactObj.getAgentConnection?.();
        if (monitorAgentConnection?.destroy) {
          monitorAgentConnection.destroy({
            success: () => resolve(),
            failure: err =>
              reject(
                err ??
                  new Error('Failed to destroy monitoring agent connection')
              ),
          });
          return;
        }
        contactObj.clear({
          success: () => resolve(),
          failure: err =>
            reject(err ?? new Error('Failed to clear monitoring contact')),
        });
      });
      setMonitoringRequestedForContactId(null);
      connectAdapter.clearExpectedMonitor();
      setMonitoringSessionContactIdByTarget(prev => {
        const next = { ...prev };
        delete next[selectedContactId];
        return next;
      });
      setListeningStartedAtByContactId(prev => {
        const next = { ...prev };
        delete next[selectedContactId];
        return next;
      });
    } catch {
      setMonitoringStopError(
        t('components.contactCenter.stopListenCallFailed')
      );
    } finally {
      setIsStoppingCallMonitoring(false);
    }
  };

  useEffect(() => {
    if (!shouldUseManagerLiveMonitor || !showTranscript) return;
    const el = managerTranscriptScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [
    shouldUseManagerLiveMonitor,
    showTranscript,
    managerLiveSegments.length,
    managerMonitorState.previousMessages.length,
  ]);

  if (!selectedCall) return null;

  return (
    <Dialog
      open={Boolean(selectedCall)}
      onClose={onClose}
      fullWidth
      maxWidth={showTranscript ? 'md' : 'sm'}
      hideBackdrop
      disableEnforceFocus
      disableScrollLock
      disableAutoFocus
      slotProps={{ root: { sx: { zIndex: 1100 } } }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {t('components.contactCenter.call')} {selectedCall.contactId}
          </Typography>
          <Box
            component="span"
            sx={{
              ml: 1,
              px: 1.5,
              py: 0.5,
              borderRadius: '4px',
              fontSize: '0.75rem',
              fontWeight: 500,
              ...callStatusStyle,
            }}
          >
            {callStatusLabel}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              flex: showTranscript ? '0 0 40%' : '1',
            }}
          >
            {selectedCall.type !== 'chat' && (
              <Typography variant="body2">
                <Box component="span" fontWeight={600}>
                  {t('components.contactCenter.phoneNumber')}:
                </Box>{' '}
                {selectedCall.clientDetails?.phoneNumber?.trim()
                  ? selectedCall.clientDetails.phoneNumber
                  : selectedCall.phoneNumber}
              </Typography>
            )}
            <Typography variant="body2">
              <Box component="span" fontWeight={600}>
                {t('common.name')}:
              </Box>{' '}
              {resolvedWorkspaceId ? (
                <ClientSpaceWorkspaceLink workspaceId={resolvedWorkspaceId}>
                  {formatCallerDisplayName(
                    selectedCall.clientDetails?.name,
                    t('components.contactCenter.unknownCaller')
                  )}
                </ClientSpaceWorkspaceLink>
              ) : (
                formatCallerDisplayName(
                  selectedCall.clientDetails?.name,
                  t('components.contactCenter.unknownCaller')
                )
              )}
            </Typography>
            {shouldUseManagerLiveMonitor && (
              <>
                <Typography variant="body2">
                  <Box component="span" fontWeight={600}>
                    {t('components.contactCenter.managerMonitorCallerLabel')}:
                  </Box>{' '}
                  {managerMonitorState.callerName ||
                    t('components.contactCenter.managerMonitorNoData')}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" fontWeight={600}>
                    {t(
                      'components.contactCenter.managerMonitorCallerPhoneLabel'
                    )}
                    :
                  </Box>{' '}
                  {managerMonitorState.callerPhoneNumber ||
                    t('components.contactCenter.managerMonitorNoData')}
                </Typography>
              </>
            )}
            <Typography variant="body2">
              <Box component="span" fontWeight={600}>
                {t('components.contactCenter.contactId')}:
              </Box>{' '}
              {selectedCall.contactId}
            </Typography>
            {showHandledBy && (
              <Typography variant="body2">
                <Box component="span" fontWeight={600}>
                  {t('components.contactCenter.handledBy')}:
                </Box>{' '}
                {selectedCall.pickedBy
                  .map(pickedBy => pickedBy.agentName)
                  .join(', ')}
              </Typography>
            )}

            {enableCustomerLink && selectedCall.contactId && (
              <HistoricalCallCustomerLinkSection
                contactId={selectedCall.contactId}
                workspaceId={resolvedWorkspaceId}
                onLinked={() => onCustomerLinked?.()}
              />
            )}

            {selectedCall.caseId && (
              <Typography variant="body2">
                <Box component="span" fontWeight={600}>
                  {t('components.contactCenter.newestCaseId')}:
                </Box>{' '}
                <Button
                  variant="text"
                  size="small"
                  onClick={() =>
                    onOpenCaseId(
                      Number(selectedCall.caseId),
                      selectedCall.contactId
                    )
                  }
                  sx={{
                    cursor: 'pointer',
                    p: 0,
                    textTransform: 'none',
                    display: 'inline',
                  }}
                >
                  {selectedCall.caseId}
                </Button>
              </Typography>
            )}

            {selectedCall.reason && (
              <Typography variant="body2">
                <Box component="span" fontWeight={600}>
                  {t('components.contactCenter.reason')}:
                </Box>{' '}
                {selectedCall.reason}
              </Typography>
            )}

            {selectedCall.outcome && (
              <Typography variant="body2">
                <Box component="span" fontWeight={600}>
                  {t('components.contactCenter.outcome')}:
                </Box>{' '}
                <Chip
                  label={selectedCall.outcome}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.75rem',
                    ...getOutcomeColor(selectedCall.outcome),
                  }}
                />
              </Typography>
            )}

            {selectedCall.caseStatus && (
              <Box>
                <Box component="span" fontWeight={600}>
                  {t('components.contactCenter.newestCaseStatus')}:
                </Box>{' '}
                <Chip
                  label={selectedCall.caseStatus}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.75rem',
                    ...getCaseStatusColor(selectedCall.caseStatus),
                  }}
                />
              </Box>
            )}

            {selectedCall.type !== 'chat' && selectedCall.isCallback && (
              <Box>
                <Box component="span" fontWeight={600}>
                  {t('components.amazonConnect.callbackLabel')}:
                </Box>{' '}
                <Chip
                  label={t('components.amazonConnect.callbackLabel')}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.75rem',
                    bgcolor: '#fef3c7',
                    color: '#92400e',
                  }}
                />
              </Box>
            )}

            {/* Case History */}
            {latestCallByContact?.caseHistory &&
              latestCallByContact.caseHistory.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: 'grey.100',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ width: 4, flexShrink: 0 }} />
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.primary"
                      sx={{
                        flex: 1,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontSize: '0.65rem',
                      }}
                    >
                      {t('components.contactCenter.caseHistory')}
                    </Typography>
                    <Chip
                      label={latestCallByContact.caseHistory.length}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        minWidth: 20,
                      }}
                    />
                  </Box>

                  {/* Column labels */}
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <Box sx={{ width: 4, flexShrink: 0 }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ flex: 1, fontSize: '0.625rem', fontWeight: 600 }}
                    >
                      {t('components.contactCenter.case')}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        width: 200,
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        textAlign: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {t('components.contactCenter.status')} /{' '}
                      {t('components.contactCenter.action')}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        width: 95,
                        fontSize: '0.625rem',
                        fontWeight: 600,
                        textAlign: 'right',
                        flexShrink: 0,
                      }}
                    >
                      {t('components.contactCenter.startTime')}
                    </Typography>
                  </Box>

                  {/* Rows */}
                  {latestCallByContact.caseHistory.map((entry, idx) => (
                    <CaseHistoryRow
                      key={`${entry.caseId}-${entry.timestamp}-${idx}`}
                      entry={entry}
                      contactId={latestCallByContact.contactId}
                      onOpenCaseId={onOpenCaseId}
                      showColumns
                    />
                  ))}
                </Box>
              )}

            {isUnanswered ? (
              <Typography variant="body2">
                <Box component="span" fontWeight={600}>
                  {t('components.contactCenter.missedAt')}:
                </Box>{' '}
                {new Date(selectedCall.startDate).toLocaleString()}
              </Typography>
            ) : (
              <>
                <Typography variant="body2">
                  <Box component="span" fontWeight={600}>
                    {t('components.contactCenter.startTime')}:
                  </Box>{' '}
                  {new Date(selectedCall.startDate).toLocaleString()}
                </Typography>

                {selectedCall.endDate && (
                  <Typography variant="body2">
                    <Box component="span" fontWeight={600}>
                      {t('components.contactCenter.endTime')}:
                    </Box>{' '}
                    {new Date(selectedCall.endDate).toLocaleString()}
                  </Typography>
                )}
              </>
            )}
            {isLiveCall && enableLiveMonitoring && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('components.contactCenter.liveMonitoring')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    size="small"
                    label={
                      hasMonitoringRequestSuccess
                        ? t('components.contactCenter.listenCallStatusActive')
                        : t('components.contactCenter.listenCallStatusIdle')
                    }
                    sx={{
                      height: 20,
                      fontSize: '0.75rem',
                      bgcolor: hasMonitoringRequestSuccess
                        ? '#dcfce7'
                        : '#f3f4f6',
                      color: hasMonitoringRequestSuccess
                        ? '#166534'
                        : '#6b7280',
                    }}
                  />
                  {hasMonitoringRequestSuccess && (
                    <Typography variant="body2" color="text.secondary">
                      {t('components.contactCenter.listenCallDuration', {
                        duration: listeningDurationLabel,
                      })}
                    </Typography>
                  )}
                </Box>
                <Box>
                  {hasMonitoringRequestSuccess ? (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleStopCallMonitoring}
                      disabled={isStoppingCallMonitoring}
                    >
                      {isStoppingCallMonitoring
                        ? t('components.contactCenter.stopListenCallRequesting')
                        : t('components.contactCenter.stopListenToCall')}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleStartCallMonitoring}
                      disabled={isStartingCallMonitoring}
                    >
                      {isStartingCallMonitoring
                        ? t('components.contactCenter.listenCallRequesting')
                        : t('components.contactCenter.listenToCall')}
                    </Button>
                  )}
                </Box>
                {monitoringRequestError && (
                  <Typography variant="body2" color="error.main">
                    {monitoringRequestError}
                  </Typography>
                )}
                {monitoringStopError && (
                  <Typography variant="body2" color="error.main">
                    {monitoringStopError}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {showTranscript && (
            <Box
              sx={{
                flex: '0 0 60%',
                borderLeft: '1px solid',
                borderColor: 'divider',
                pl: 2,
                maxHeight: 400,
                overflowY: 'auto',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                {shouldUseManagerLiveMonitor
                  ? t('components.contactCenter.liveTranscriptMonitor')
                  : t('components.contactCenter.transcript')}
              </Typography>
              {shouldUseManagerLiveMonitor ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={managerMonitorStatusLabel}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.75rem',
                        bgcolor:
                          managerMonitorState.status === 'live'
                            ? '#dcfce7'
                            : managerMonitorState.status === 'ended'
                              ? '#e0e7ff'
                              : managerMonitorState.status === 'error'
                                ? '#fee2e2'
                                : '#fef9c3',
                        color:
                          managerMonitorState.status === 'live'
                            ? '#166534'
                            : managerMonitorState.status === 'ended'
                              ? '#3730a3'
                              : managerMonitorState.status === 'error'
                                ? '#991b1b'
                                : '#854d0e',
                      }}
                    />
                    {(managerMonitorState.status === 'ended' ||
                      managerMonitorState.status === 'error') && (
                      <Button
                        size="small"
                        variant="text"
                        color="inherit"
                        onClick={restartManagerMonitor}
                        sx={{ textTransform: 'none', minWidth: 'auto', p: 0 }}
                      >
                        {t('components.contactCenter.restartMonitor')}
                      </Button>
                    )}
                  </Box>
                  <TranscriptContent
                    previousMessages={managerMonitorState.previousMessages}
                    segments={managerLiveSegments}
                    contactId={selectedCall.contactId}
                    enableNotes={false}
                    size="compact"
                    scrollContainerRef={managerTranscriptScrollRef}
                    scrollBoxSx={{ minHeight: '14rem', maxHeight: '22rem' }}
                  />
                </Box>
              ) : (
                <StoredTranscriptView
                  contactId={selectedCall.contactId}
                  callType={selectedCall.type}
                />
              )}
            </Box>
          )}
        </Box>

        {!isLiveCall && selectedCall.hasRecording && (
          <Box sx={{ mt: 2 }}>
            {showRecordingStatus && isRecordingFetching && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 2,
                }}
              >
                <CircularProgress size={20} />
              </Box>
            )}

            {showRecordingStatus &&
              !isRecordingFetching &&
              isRecordingError && (
                <Typography variant="body2" color="error.main">
                  {selectedCall && !selectedCall.hasRecording
                    ? t('components.contactCenter.noRecordingFound')
                    : t('components.contactCenter.errorLoadingRecording')}
                </Typography>
              )}

            {!isRecordingFetching &&
              !isRecordingError &&
              showRecordingStatus &&
              recordingPresignedUrls.length === 0 && (
                <Typography variant="body2" color="error.main">
                  {t('components.contactCenter.noRecordingFound')}
                </Typography>
              )}

            {!isRecordingFetching &&
              !isRecordingError &&
              showRecordingStatus &&
              recordingPresignedUrls.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ mb: 1 }}
                  >
                    {t('components.contactCenter.callRecordingTitle')}
                  </Typography>
                  {recordingPresignedUrls.map((recording, index) => {
                    const pickedByName =
                      selectedCall.pickedBy[index]?.agentName ?? t('common.na');
                    const expiryStateForRecording = getExpiryState(
                      recording.expiresAt
                    );
                    const expiryLabelForRecording = getRecordingExpiryLabel(
                      expiryStateForRecording
                    );

                    return (
                      <Box
                        key={`${recording.recordingPath}-${index}`}
                        sx={{ mt: index === 0 ? 0 : 2 }}
                      >
                        {showHandledBy && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <Box component="span" fontWeight={600}>
                              {t('components.contactCenter.handledBy')}:
                            </Box>{' '}
                            {pickedByName}
                          </Typography>
                        )}

                        <MixedAudioPlayer src={recording.url} />

                        {expiryLabelForRecording && (
                          <Typography
                            variant="caption"
                            color={
                              expiryStateForRecording?.isExpired
                                ? 'error.main'
                                : 'text.secondary'
                            }
                            sx={{ mt: 1, display: 'block' }}
                          >
                            {expiryLabelForRecording}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {t('common.close')}
        </Button>
        <Button
          variant={showTranscript ? 'contained' : 'outlined'}
          color={showTranscript ? 'secondary' : 'inherit'}
          size="small"
          onClick={() => setShowTranscript(!showTranscript)}
        >
          {showTranscript
            ? t('components.contactCenter.hideTranscript')
            : isLiveCall
              ? t('components.contactCenter.showLiveTranscript')
              : t('components.contactCenter.viewTranscript')}
        </Button>

        {!isLiveCall && selectedCall.hasRecording && (
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={handleLoadRecording}
            disabled={!selectedCall.hasRecording}
          >
            {t('components.contactCenter.playRecording')}
          </Button>
        )}

        {showCreateCaseButton && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={onOpenCreateCase}
          >
            {t('components.contactCenter.createCase')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
